import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Tooltip } from 'antd';

function formatCssLength(length: string | number) {
  if (typeof length === 'string') return length;

  return `${length}px`;
}

const NetworkQualityLevelDiv = styled.div<{
  width: string | number;
  height: string | number;
  gap: string | number;
  activeColour: string;
  inactiveColour: string;
}>`
  display: inline-flex;
  gap: ${({ gap }) => formatCssLength(gap)};
  align-items: flex-end;
  width: ${({ width }) => formatCssLength(width)};
  height: ${({ height }) => formatCssLength(height)};
  outline: none;

  div {
    border: 1px solid ${({ activeColour }) => activeColour};
    width: 100%;
    background-color: ${({ inactiveColour }) => inactiveColour};
  }

  div.on {
    background-color: ${({ activeColour }) => activeColour};
  }
`;

const BARS_ARRAY = [0, 1, 2, 3, 4];

interface NetworkQualityLevelInterface {
  /**
   * The quality of the signal
   */
  quality?: 0 | 1 | 2 | 3 | 4 | 5;

  /**
   * The latency of the signal - the larger of the Twilio audio and video latency
   */
  latency?: 0 | 1 | 2 | 3 | 4 | 5;

  /**
   * The width of the component (defaults to 100%)
   */
  width?: number | string;

  /**
   * The height of the component (defaults to 100%)
   */
  height?: number | string;

  /**
   * The gap between bars (default to 3px)
   */
  gap?: number | string;

  /**
   * The border colour and fill colour of active bars.
   *
   * @default 'black'
   */
  activeColour?: string;

  /**
   * The background colour of inactive bars.
   *
   * @default 'white'
   */
  inactiveColour?: string;
}

const QualityLevels = ['Bad', 'Poor', 'Average', 'Good', 'Excellent'];

const TooltipDefinitionList = styled.dl`
  dd {
    margin-left: 2rem;
  }
  dt: {
    font-weight: normal;
  }
`;

export default function SignalQuality({
  quality,
  latency,
  width = '100%',
  height = '100%',
  gap = 3,
  activeColour,
  inactiveColour
}: NetworkQualityLevelInterface) {
  const { t } = useTranslation('common');
  if (quality === null || quality === undefined || latency === null || latency === undefined) return null;

  const qualityLabel = QualityLevels[quality - 1] || 'Off';
  const latencyLabel = QualityLevels[latency - 1] || 'Unknown';

  return (
    <Tooltip
      trigger={['hover', 'focus']}
      title={
        <TooltipDefinitionList>
          <dt>{t('Signal Quality')}</dt>
          <dd>{qualityLabel}</dd>
          <dt>{t('Latency')}</dt>
          <dd>{latencyLabel}</dd>
        </TooltipDefinitionList>
      }
    >
      <NetworkQualityLevelDiv
        width={width}
        height={height}
        gap={gap}
        activeColour={activeColour || 'black'}
        inactiveColour={inactiveColour || 'white'}
        role="img"
        tabIndex={0}
        aria-label={t('Signal quality: {{qualityLabel}}. Latency: {{latencyLabel}}.', {
          qualityLabel,
          latencyLabel
        })}
      >
        {BARS_ARRAY.map((level) => (
          <div
            className={quality > level ? 'on' : 'off'}
            key={level}
            aria-hidden="true"
            style={{
              height: `${20 * (level + 1)}%`
            }}
          >
            &nbsp;
          </div>
        ))}
      </NetworkQualityLevelDiv>
    </Tooltip>
  );
}
